import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  ArrowLeft, 
  Phone, 
  Mail, 
  Car, 
  DollarSign,
  Edit,
  Plus,
  MapPin,
  Clock,
  Calendar,
  User,
  FileText,
  Tag,
  X
} from 'lucide-react';

const ContactDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [allTags, setAllTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: '',
    tags: []
  });

  useEffect(() => {
    fetchContact();
    fetchTags();
  }, [id]);

  const fetchContact = async () => {
    try {
      const response = await axios.get(`${API}/contacts/${id}`);
      setContact(response.data);
      setFormData({
        name: response.data.name,
        email: response.data.email || '',
        phone: response.data.phone || '',
        notes: response.data.notes || '',
        tags: response.data.tags || []
      });
    } catch (error) {
      toast.error('Failed to load contact');
      navigate('/contacts');
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await axios.get(`${API}/contacts/tags`);
      setAllTags(response.data);
    } catch (error) {
      console.error('Failed to load tags');
    }
  };

  const addTagToForm = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, newTag.trim()] });
      setNewTag('');
    }
  };

  const removeTagFromForm = (tagToRemove) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tagToRemove) });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    try {
      await axios.put(`${API}/contacts/${id}`, formData);
      toast.success('Contact updated successfully');
      setShowEditModal(false);
      fetchContact();
      fetchTags();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update contact');
    }
  };

  const handleAddRide = () => {
    navigate('/rides/new', { 
      state: { 
        prefillContact: {
          client_name: contact.name,
          client_email: contact.email,
          client_phone: contact.phone
        }
      }
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'available': { color: 'bg-blue-100 text-blue-800', label: 'Available' },
      'claimed': { color: 'bg-gold-100 text-gold-800', label: 'Claimed' },
      'assigned': { color: 'bg-purple-100 text-purple-800', label: 'Assigned' },
      'picked_up': { color: 'bg-orange-100 text-orange-800', label: 'In Progress' },
      'completed': { color: 'bg-green-100 text-green-800', label: 'Completed' },
      'requested': { color: 'bg-indigo-100 text-indigo-800', label: 'Requested' },
      'cancelled': { color: 'bg-red-100 text-red-800', label: 'Cancelled' }
    };
    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', label: status };
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading contact...</div>
      </div>
    );
  }

  if (!contact) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/contacts')}
          className="flex items-center gap-1"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
      </div>

      {/* Contact Info Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                    <User className="w-6 h-6 text-blue-600" />
                    {contact.name}
                  </h2>
                  <div className="mt-3 space-y-2">
                    {contact.email && (
                      <p className="text-gray-600 flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        <a href={`mailto:${contact.email}`} className="hover:text-blue-600">
                          {contact.email}
                        </a>
                      </p>
                    )}
                    {contact.phone && (
                      <p className="text-gray-600 flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        <a href={`tel:${contact.phone}`} className="hover:text-blue-600">
                          {contact.phone}
                        </a>
                      </p>
                    )}
                    {contact.notes && (
                      <p className="text-gray-600 flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        {contact.notes}
                      </p>
                    )}
                    {contact.tags && contact.tags.length > 0 && (
                      <div className="flex items-center gap-2 mt-2">
                        <Tag className="w-4 h-4 text-gray-400" />
                        <div className="flex flex-wrap gap-1">
                          {contact.tags.map(tag => (
                            <Badge key={tag} variant="secondary">{tag}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowEditModal(true)}
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Edit
                </Button>
              </div>
            </div>

            {/* Stats */}
            <div className="flex flex-row md:flex-col gap-4 md:gap-2 md:text-right">
              <div className="bg-blue-50 px-4 py-3 rounded-lg">
                <div className="flex items-center gap-2 justify-center md:justify-end">
                  <Car className="w-5 h-5 text-blue-600" />
                  <span className="text-2xl font-bold text-blue-600">{contact.total_rides || 0}</span>
                </div>
                <p className="text-sm text-gray-600">Total Rides</p>
              </div>
              <div className="bg-green-50 px-4 py-3 rounded-lg">
                <div className="flex items-center gap-2 justify-center md:justify-end">
                  <DollarSign className="w-5 h-5 text-green-600" />
                  <span className="text-2xl font-bold text-green-600">
                    ${(contact.total_spent || 0).toFixed(2)}
                  </span>
                </div>
                <p className="text-sm text-gray-600">Total Spent</p>
              </div>
            </div>
          </div>

          {/* Quick Action */}
          <div className="mt-6 pt-6 border-t">
            <Button
              onClick={handleAddRide}
              className="bg-green-600 hover:bg-green-700"
              data-testid="add-ride-for-contact"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Ride for {contact.name}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Ride History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Car className="w-5 h-5" />
            Ride History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {contact.rides && contact.rides.length > 0 ? (
            <div className="space-y-4">
              {contact.rides.map((ride) => (
                <div 
                  key={ride.id} 
                  className="border rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => navigate(`/rides/edit/${ride.id}`)}
                >
                  <div className="flex flex-col md:flex-row justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        <span className="font-medium">
                          {format(new Date(ride.date), 'EEE, MMM dd, yyyy')}
                        </span>
                        <Clock className="w-4 h-4 text-gray-500 ml-2" />
                        <span className="text-gray-600">
                          {format(new Date(ride.date), 'h:mm a')}
                        </span>
                        {getStatusBadge(ride.status)}
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <p className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-green-600" />
                          <strong>From:</strong> {ride.pickup_location}
                        </p>
                        <p className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-red-600" />
                          <strong>To:</strong> {ride.dropoff_location}
                        </p>
                      </div>
                      {ride.notes && (
                        <p className="text-sm text-gray-500 mt-2 italic">
                          "{ride.notes}"
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-green-600">
                        ${(ride.total_amount || 0).toFixed(2)}
                      </p>
                      <p className="text-sm text-gray-500">
                        {ride.miles_driven} miles • {(ride.time_hours * 60).toFixed(0)} min
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Car className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No rides yet</h3>
              <p className="text-gray-500 mb-4">
                This contact hasn't taken any rides yet
              </p>
              <Button onClick={handleAddRide} className="bg-green-600 hover:bg-green-700">
                <Plus className="w-4 h-4 mr-2" />
                Add First Ride
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Contact Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Contact</DialogTitle>
            <DialogDescription>
              Update contact information
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdate}>
            <div className="space-y-4 py-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Client name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tags
                </label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Add a tag"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTagToForm())}
                  />
                  <Button type="button" variant="outline" onClick={addTagToForm}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {formData.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {formData.tags.map(tag => (
                      <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                        {tag}
                        <button type="button" onClick={() => removeTagFromForm(tag)} className="hover:text-red-600">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any notes about this client"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button type="submit">
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContactDetail;
